import React, { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Loader2, Save, RotateCcw, Check, Send, CheckCircle2, XCircle, MoreHorizontal, Eye } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Popover } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { DayRacksCard, type RacksFocusRequest } from '@/components/report/day/DayRacksCard';
import { DayQueueCard } from '@/components/report/day/DayQueueCard';
import { useT } from '@/contexts/I18nContext';
import {
  patchDailyReport,
  confirmDailyReport,
  cancelDailyReport,
  sendDailyReport,
  getQueuePhotoPreview,
  type DailyReportDto,
} from '@/api/dailyReports';
import { getLayoutDetail, getQueueDetail } from '@/api/analytics';

import { FormattedBodyEditor, type EditorMode } from './FormattedBodyEditor';
import {
  stripHiddenSections,
  stripHiddenRows,
  extractReportLinks,
  msToMskHhmm,
  type RackOption,
} from './constructor/lineModel';

const CARD_TITLE = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

// Denser queue series for the consult chart than the 15-min report default —
// validators eyeball a finer-grained day (5-min buckets).
const CONSULT_QUEUE_BUCKET_MS = 5 * 60 * 1000;

/**
 * V1 report-validation editor for a single daily report.
 *
 * Left: a formatted editor over `body_html` that saves via the existing
 * `patchDailyReport` path (the same write Telegram editing uses — facts-model-free,
 * prod-safe). Right: read-only consult charts (Выкладка + Очередь) reused from
 * `/usage/report`, purely for reference while editing — no annotation here (that's V3).
 *
 * `onSave` is the seam the `shell-offline` peer swaps in to route edits through the
 * offline store; the default persists straight to the backend.
 */
export function ReportEditorV1({
  report,
  zoneId,
  day,
  onSave,
  onConfirm,
  onCancel,
  onSend,
  onActioned,
  mode: modeProp,
  onModeChange,
}: {
  report: DailyReportDto;
  zoneId: string;
  day: string;
  onSave?: (reportId: number, bodyHtml: string) => Promise<void>;
  // Delivery seams — default to the direct API; shell-offline overrides them to
  // route through the offline mark/submit queue.
  onConfirm?: (reportId: number) => Promise<void>;
  onCancel?: (reportId: number) => Promise<void>;
  onSend?: (reportId: number) => Promise<void>;
  onActioned?: () => void;
  /** Controlled mode (item 10) — the parent page lifts this so it survives
   *  this component unmounting/remounting across date/branch switches.
   *  Omit for uncontrolled (internal state, default 'constructor'). */
  mode?: EditorMode;
  onModeChange?: (mode: EditorMode) => void;
}) {
  const { t } = useT();
  const [bodyHtml, setBodyHtml] = useState(report.body_html);
  const [status, setStatus] = useState<DailyReportDto['status']>(report.status);
  const [action, setAction] = useState<null | 'confirm' | 'cancel' | 'send'>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Seeded from the stored sent_at so a reload shows the real delivery
  // state, not "never sent" (#23).
  const [sentAt, setSentAt] = useState<number | null>(report.sent_at ? Date.parse(report.sent_at) : null);
  // Sections the validator excluded (D3). Kept in the editing model (not in `bodyHtml`)
  // so toggling is reversible in-session; stripped from the saved output only. Durable
  // hide across reloads belongs to the shell-offline store — this is the in-session cut.
  const [hiddenSections, setHiddenSections] = useState<Set<string>>(new Set());
  // Rows/blocks removed in the constructor (Alexey 2026-09-04 spec: per-row
  // "убрать", greyed with undo). Keyed by the block's text, stripped on save.
  const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set());
  const [modeInternal, setModeInternal] = useState<EditorMode>('constructor');
  const mode = modeProp ?? modeInternal;
  const setMode = onModeChange ?? setModeInternal;
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const modeMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // item 11 (round-2): "Сбросить" only reverted `bodyHtml`/hidden-sections —
  // the interactive charts' OWN local UI state (which segment/keypoint is
  // selected, in-progress segment lists seeded from auto-detection, etc.,
  // all owned by FoodStatusChartBlock/KeypointChartBlock/QueueChartBlock)
  // kept whatever it was, now visibly out of sync with the reverted text.
  // Bumping this key forces the whole editor subtree to remount on reset,
  // clearing every bit of that local state along with the text.
  const [resetNonce, setResetNonce] = useState(0);
  // Constructor category-link → select that rack in the consult panel on the
  // right (Alexey 2026-09-07), instead of the link's new-tab default.
  const [racksFocus, setRacksFocus] = useState<RacksFocusRequest | null>(null);
  const racksFocusNonce = useRef(0);
  const openRackInPanel = (
    rackName: string | undefined,
    window: [number, number] | undefined,
    rackId?: number,
  ) => {
    racksFocusNonce.current += 1;
    setRacksFocus({
      domain: window ?? [0, 0],
      window,
      rackName,
      rackId,
      inPanel: true,
      nonce: racksFocusNonce.current,
    });
  };

  // Re-seed if the underlying report changes (different day/zone selected).
  useEffect(() => {
    setBodyHtml(report.body_html);
    setHiddenSections(new Set());
    setError(null);
    setSavedAt(null);
    setStatus(report.status);
    setActionError(null);
    setSentAt(null);
    setResetNonce((n) => n + 1); // switching reports is also a full UI-state reset
  }, [report.id, report.body_html, report.status]);

  // Layout detail → problem-zone dropdown options + the working-hours open time
  // (keypoint "после открытия" calc). Grafana url per rack is best-effort from the
  // report's own links today; the full per-rack link needs a BE affordance.
  const { data: layout } = useSWR(
    zoneId ? ['rv-layout', zoneId, day] : null,
    () => getLayoutDetail(zoneId, day, day).then((r) => r.data),
    { revalidateOnFocus: false },
  );
  const racksByType = useMemo<Record<string, RackOption[]>>(() => {
    const linkMap = extractReportLinks(report.body_html);
    const out: Record<string, RackOption[]> = {};
    for (const rack of layout?.racks ?? []) {
      const type = rack.rack_type ?? '';
      (out[type] ??= []).push({ name: rack.name, url: linkMap[rack.name] ?? null });
    }
    return out;
  }, [layout, report.body_html]);
  const openTime = layout?.working_hours?.start
    ? msToMskHhmm(new Date(layout.working_hours.start).getTime())
    : null;

  // Queue cashier steps → max cashiers (warns when a second cashier is impossible).
  const { data: queueOverlay } = useSWR(
    zoneId ? ['rv-queue-cashiers', zoneId, day] : null,
    () =>
      getQueueDetail(zoneId, day, day, 15 * 60 * 1000, { photoCount: 1 }).then(
        (r) => r.data.overlay ?? [],
      ),
    { revalidateOnFocus: false },
  );
  const maxCashiers = useMemo<number | null>(() => {
    if (!queueOverlay) return null;
    const vals = queueOverlay.map((p) => p.cashier_count ?? 0);
    return vals.length ? Math.max(...vals) : null;
  }, [queueOverlay]);

  const dirty = bodyHtml !== report.body_html || hiddenSections.size > 0 || hiddenRows.size > 0;

  // Preview modal (Alexey 2026-09-04 spec): the FULL report exactly as it
  // will be saved — header lines included, hidden sections/rows stripped.
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewRows = useMemo(
    () => stripHiddenRows(stripHiddenSections(bodyHtml, hiddenSections), hiddenRows).split('\n'),
    [bodyHtml, hiddenSections, hiddenRows],
  );

  const toggleSection = (key: string) =>
    setHiddenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleRow = (key: string) =>
    setHiddenRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const reset = () => {
    setBodyHtml(report.body_html);
    setHiddenSections(new Set());
    setHiddenRows(new Set());
    setResetNonce((n) => n + 1); // item 11: also clears interactive-editor UI state (see key below)
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const output = stripHiddenRows(stripHiddenSections(bodyHtml, hiddenSections), hiddenRows);
    try {
      if (onSave) {
        await onSave(report.id, output);
      } else {
        await patchDailyReport(report.id, output);
      }
      setSavedAt(Date.now());
    } catch {
      setError(t('reportValidation.editor.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Delivery actions. Default to the direct API (confirm/cancel/send); shell-offline
  // overrides via the on* seams to route through the offline mark/submit queue.
  const runAction = async (
    kind: 'confirm' | 'cancel' | 'send',
    seam: ((id: number) => Promise<void>) | undefined,
    direct: (id: number) => Promise<DailyReportDto>,
    nextStatus: DailyReportDto['status'] | null,
  ) => {
    setAction(kind);
    setActionError(null);
    try {
      let delivered = true;
      if (seam) await seam(report.id);
      else {
        const dto = await direct(report.id);
        setStatus(dto.status);
        // #23: /send/ answers 200 even when the prod-chat push failed (the
        // report is confirmed either way) — it flags delivery_failed instead.
        // Before this the badge flipped to «Отправлено» on any 200, so a
        // failed delivery looked exactly like a successful one (#344).
        if (kind === 'send' && dto.delivery_failed) {
          delivered = false;
          setActionError(
            `${t('reportValidation.action.sendDeliveryFailed')}${dto.delivery_error ? `: ${dto.delivery_error}` : ''}`,
          );
        }
      }
      if (nextStatus) setStatus(nextStatus);
      if (kind === 'send' && delivered) setSentAt(Date.now());
      onActioned?.();
    } catch {
      setActionError(t(`reportValidation.action.${kind}Failed`));
    } finally {
      setAction(null);
    }
  };

  const busy = action != null;
  const isInteractive = mode === 'interactive';

  const hold = report.photo_highlights?.auto_send_hold;
  return (
    <div className="flex flex-col gap-4">
      {/* Автоотправка не отправила этот отчёт сама (Алексей 27.10 #3005) — пояснение, почему он на проверке. */}
      {hold && !report.sent_at && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          <div className="font-medium">Не ушёл автоматически — проверьте</div>
          <ul className="mt-1 list-disc pl-5">
            {hold.reasons.map((r) => <li key={r}>{r}</li>)}
          </ul>
        </div>
      )}
      {/* ── Delivery action bar (single unified row — item 6) ── */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-2">
        <StatusBadge status={status} sent={sentAt != null} />
        {/* item 8 (round-1) / item 2 (round-2, "потоньше, покрасивее"): only
            Конструктор/Интерактив are user-facing, as a 2-button toggle
            instead of a 4-item dropdown. Round-2 polish: a slimmer pill
            (was a squared-off, fairly tall bordered box) — less vertical
            padding, rounded-full track, a floating active pill instead of a
            hard-edged fill, smaller/tighter type. Форматирование/Исходный
            текст stay reachable (never deleted — the raw-text escape hatch
            is D5's prod-safety net) behind the small "⋯" overflow next to
            the toggle. */}
        <div
          role="tablist"
          aria-label={t('reportValidation.editor.mode')}
          className="inline-flex items-center gap-0.5 rounded-full border border-border bg-muted/40 p-0.5"
        >
          {(['constructor', 'interactive'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => setMode(m)}
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium transition-all',
                mode === m
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(`reportValidation.editor.${m}`)}
            </button>
          ))}
        </div>
        <Tooltip content={t('reportValidation.editor.moreModes')}><button
          ref={modeMenuTriggerRef}
          type="button"
          onClick={() => setModeMenuOpen((v) => !v)}
          aria-label={t('reportValidation.editor.moreModes')}
          className={cn(
            'inline-flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground',
            (mode === 'rich' || mode === 'raw') && 'border-primary/50 text-primary',
          )}
        >
          <MoreHorizontal className="size-4" />
        </button></Tooltip>
        <Popover open={modeMenuOpen} onOpenChange={setModeMenuOpen} triggerRef={modeMenuTriggerRef} align="start">
          <div className="flex flex-col p-1 text-sm">
            {(['rich', 'raw'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setModeMenuOpen(false);
                }}
                className={cn(
                  'rounded px-3 py-1.5 text-left transition hover:bg-muted',
                  mode === m && 'text-primary',
                )}
              >
                {t(`reportValidation.editor.${m === 'rich' ? 'formatted' : 'raw'}`)}
              </button>
            ))}
          </div>
        </Popover>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* item 15: "Текст отчёта" is no longer its own card in Интерактив
              — its reset/save controls fold into this shared header row
              instead (Конструктор/Форматирование/Исходный текст keep their
              own card header, unaffected). */}
          {isInteractive && dirty && (
            <Button type="button" variant="ghost" size="sm" onClick={reset} disabled={saving}>
              <RotateCcw className="size-3.5" />
              {t('reportValidation.editor.reset')}
            </Button>
          )}
          {isInteractive && (
            <Button type="button" size="sm" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              {t('reportValidation.editor.save')}
            </Button>
          )}
          {actionError && <span className="text-xs text-destructive">{actionError}</span>}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || status === 'cancelled'}
            onClick={() => runAction('confirm', onConfirm, confirmDailyReport, 'confirmed')}
          >
            {action === 'confirm' ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="size-3.5" />
            )}
            {t('reportValidation.action.confirm')}
          </Button>
          {(() => {
            // J3.6: отчёт старше недели клиенту не отправляется (сервер только
            // подтвердит) — кнопка неактивна, почему — в быстрой подсказке.
            const tooOld = (Date.now() - new Date(`${report.period_day}T00:00:00`).getTime()) / 86_400_000 > 8;
            const sendButton = (
              <Button
                type="button"
                size="sm"
                disabled={busy || status === 'cancelled' || report.auto_no_data || tooOld}
                onClick={() => runAction('send', onSend, sendDailyReport, null)}
              >
                {action === 'send' ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Send className="size-3.5" />
                )}
                {t('reportValidation.action.send')}
              </Button>
            );
            return tooOld
              ? <Tooltip content={t('reportValidation.action.sendTooOld')} openDelay={0}><span tabIndex={0}>{sendButton}</span></Tooltip>
              : sendButton;
          })()}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy || status === 'cancelled'}
            onClick={() => runAction('cancel', onCancel, cancelDailyReport, 'cancelled')}
          >
            <XCircle className="size-3.5" />
            {t('reportValidation.action.cancel')}
          </Button>
        </div>
      </div>

      <div className={cn('grid grid-cols-1 gap-4', !isInteractive && 'xl:grid-cols-2')}>
      {/* ── Editor ── */}
      <Card className="flex flex-col">
        {!isInteractive && (
          <CardHeader className="flex flex-row items-center justify-between gap-3 pb-2">
            <CardTitle className={CARD_TITLE}>{t('reportValidation.editor.title')}</CardTitle>
            <div className="flex items-center gap-2">
              {dirty && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={reset}
                  disabled={saving}
                >
                  <RotateCcw className="size-3.5" />
                  {t('reportValidation.editor.reset')}
                </Button>
              )}
              <Button type="button" size="sm" onClick={handleSave} disabled={!dirty || saving}>
                {saving ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Save className="size-3.5" />
                )}
                {t('reportValidation.editor.save')}
              </Button>
            </div>
          </CardHeader>
        )}
        <CardContent
          className="flex flex-1 flex-col gap-2"
        >
          {/* 2026-09-03 (Alexey, bug caught via the queue-photo preview
              review): the card used to cap at 70vh with its OWN internal
              scroll in constructor mode — "должен быть на всю карточку и
              растягивать её, если не влезает, скроллить со всей страницей."
              Card now grows to its content and scrolls with the page in
              BOTH modes, matching what Интерактив already did. */}
          <FormattedBodyEditor
            key={resetNonce}
            value={bodyHtml}
            onChange={setBodyHtml}
            day={day}
            zoneId={zoneId}
            racksByType={racksByType}
            openTime={openTime}
            maxCashiers={maxCashiers}
            hiddenSections={hiddenSections}
            onToggleSection={toggleSection}
            hiddenRows={hiddenRows}
            onToggleRow={toggleRow}
            disabled={saving}
            mode={mode}
            onModeChange={setMode}
            onOpenRack={openRackInPanel}
          />
          <div className="flex min-h-4 items-center justify-between gap-2 text-xs">
            <span>
              {error && <span className="text-destructive">{error}</span>}
              {!error && savedAt && !dirty && (
                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <Check className="size-3.5" />
                  {t('reportValidation.editor.saved')}
                </span>
              )}
              {!error && dirty && (
                <span className="text-muted-foreground">
                  {t('reportValidation.editor.unsaved')}
                </span>
              )}
            </span>
            <Button type="button" variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
              <Eye className="size-3.5" />
              {t('reportValidation.editor.preview')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('reportValidation.editor.preview')}</DialogTitle>
          </DialogHeader>
          <div className="report-body text-sm leading-relaxed">
            {previewRows.map((row, i) => {
              const trimmed = row.trim();
              if (trimmed === '') return <div key={i} className="h-3" />;
              const html = trimmed.replace(/<a\s+/gi, '<a target="_blank" rel="noreferrer" ');
              return trimmed.startsWith('<blockquote') ? (
                <blockquote key={i} className="mt-0.5" dangerouslySetInnerHTML={{ __html: html }} />
              ) : (
                <div key={i} dangerouslySetInnerHTML={{ __html: html }} />
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

        {/* ── Consult charts (read-only reference) — Интерактив already shows this
             data per-line via its own charts, so they'd be redundant there. ── */}
        {!isInteractive && (
          <div className="flex flex-col gap-2">
            <DayRacksCard
              zoneId={zoneId}
              day={day}
              compact
              focusRequest={racksFocus}
              onFocusHandled={() => setRacksFocus(null)}
            />
            <DayQueueCard zoneId={zoneId} day={day} bucketMs={CONSULT_QUEUE_BUCKET_MS} compact />
            {/* Below the main consult charts (Alexey 2026-09-02: it had been
                first, pushing the charts down — "вытеснив основные графики"). */}
            {/* 2026-09-03 (Alexey): "превью фото для тг должен начинать
                грузиться только у уже подтвержденного отчета" — this render
                triggers a real S3 fetch + full PIL composite server-side;
                doing that unconditionally for every PENDING report a
                validator opens (most of which never get this far) was
                avoidable backend load, not just an idle preview. */}
            {report.status === 'confirmed' && report.queue_photo_preview_url && (
              <QueuePhotoOverlayPreview reportId={report.id} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Preview of the composited "Самая длинная очередь" Telegram photo —
 * polygon outline + corner pill/robot badge, rendered server-side on
 * demand (queue_photo_overlay.py). Purely a review surface: while
 * PeriodicReportsRuntimeSettings.queue_photo_send_enabled is off (the
 * default), this is the ONLY place the composite is visible — the real
 * prod-chat send doesn't attach it yet (Alexey 2026-09-02).
 *
 * Blob-fetched (not a plain <img src>) because the daily-reports API
 * requires the JWT Authorization header — same pattern as
 * getVisitSendPreview / VisitDetailPage's send-preview dialog.
 */
function QueuePhotoOverlayPreview({ reportId }: { reportId: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setFailed(false);
    setUrl(null);
    getQueuePhotoPreview(reportId)
      .then(resp => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(resp.data);
        setUrl(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [reportId]);

  if (failed) return null;

  return (
    <Card>
      <CardHeader className="pt-2 pb-1">
        <CardTitle className={CARD_TITLE}>Превью фото в ТГ (очередь)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {url ? (
          <img src={url} alt="" className="w-full rounded-lg" />
        ) : (
          <div className="h-40 animate-pulse rounded-lg bg-muted" />
        )}
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status, sent }: { status: DailyReportDto['status']; sent: boolean }) {
  const { t } = useT();
  if (sent) return <Badge variant="success">{t('reportValidation.action.sent')}</Badge>;
  switch (status) {
    case 'confirmed':
      return <Badge variant="success">{t('summaries.statusConfirmed')}</Badge>;
    case 'cancelled':
      return <Badge variant="muted">{t('summaries.statusCancelled')}</Badge>;
    default:
      return <Badge variant="secondary">{t('summaries.statusPending')}</Badge>;
  }
}
